import { OverflowMenu, OverflowMenuItem } from "@carbon/react";
import React from "react";

import { AuthorizedItem } from "src/authentication/AuthorizedMenu";
import { Scope, ScopeResource } from "src/authentication/PermissionsProvider";
import { HeadlessWidget } from "src/dynamic-view-renderer/headlessWidgets/HeadlessWidget.typings";
import { HeadlessWidgetRenderer } from "src/dynamic-view-renderer/headlessWidgets/HeadlessWidgetRenderer";
import { useT } from "src/internationalization";
import { CaseListTableConfig, CaseSummary, CaseTypeDefinition } from "src/types/case";

export const useGetColumns = ({
  handleOnRowClick,
  setSelectedCase,
  setSelectedCaseIndex,
  onOpenExecuteRule,
  caseListTable,
  caseTypeDefinitions,
  headlessWidgets,
  searchTerm,
}: {
  handleOnRowClick: (id: string, index: number, caseTypeCode: string) => void;
  setSelectedCase: (caseItem: CaseSummary) => void;
  setSelectedCaseIndex: (index: number) => void;
  onOpenExecuteRule: (caseItem: CaseSummary) => void;
  caseListTable?: CaseListTableConfig;
  caseTypeDefinitions: CaseTypeDefinition[];
  headlessWidgets: HeadlessWidget[];
  searchTerm?: string;
}) => {
  const t = useT();

  const { columns, sortByMap, reverseSortByMap } = React.useMemo(() => {
    // Find the case type configuration

    const sortByMap: Record<string, string> = {};
    const reverseSortByMap: Record<string, string> = {};

    const dynamicColumns = caseListTable!.columns.reduce((acc, column) => {
      const widget = headlessWidgets.find((w) => w.code === column.headlessWidgetCode);
      if (!widget) return acc;

      if (column.sortBy) {
        sortByMap[column.headlessWidgetCode] = column.sortBy;
        reverseSortByMap[column.sortBy] = column.headlessWidgetCode;
      }

      return {
        ...acc,
        [column.headlessWidgetCode]: {
          label: column.headerLabel,
          flexGrow: column.flexGrow ?? (column.width && column.width !== 1 ? 0 : 1),
          flexShrink: column.flexShrink ?? (column.width && column.width !== 1 ? 0 : 1),
          width: column.width || 1,
          className: "text-sm",
          render: (row: CaseSummary) => (
            <HeadlessWidgetRenderer
              widget={widget}
              searchTerm={searchTerm}
              data={{
                root: row,
                elements: row.elements,
                propertyValues: row.properties,
                caseTypeDefinitions: caseTypeDefinitions,
              }}
            />
          ),
          sorter: column.sorter,
        },
      };
    }, {});

    // Add actions column
    const columns = {
      ...dynamicColumns,
      actions: {
        label: "",
        width: 80,
        render: (row, index) => (
          <div key={row.id} id={row.id}>
            <OverflowMenu flipped={true} onClick={(e) => e.stopPropagation()}>
              <OverflowMenuItem
                itemText={<div>{t("page.cases.tab.requests.table.menu.detail")}</div>}
                onClick={(e) => {
                  if (index != null) handleOnRowClick(row.id, index, row.typeCode);
                  e.stopPropagation();
                }}
              />

              <AuthorizedItem
                requiredPermissions={[{ resource: ScopeResource.RULE, scopes: [Scope.EXECUTE] }]}
              >
                <OverflowMenuItem
                  itemText={<div>{t("page.cases.tab.requests.table.menu.testRule")}</div>}
                  onClick={(e) => {
                    onOpenExecuteRule(row);
                    e.stopPropagation();
                  }}
                />
              </AuthorizedItem>

              <AuthorizedItem
                requiredPermissions={[
                  { resource: ScopeResource.SCENARIO, scopes: [Scope.EXECUTE] },
                ]}
              >
                <OverflowMenuItem
                  itemText={<div>{t("page.cases.tab.requests.table.menu.run")}</div>}
                  onClick={(e) => {
                    setSelectedCase(row);
                    e.stopPropagation();
                    setSelectedCaseIndex(index);
                  }}
                />
              </AuthorizedItem>
            </OverflowMenu>
          </div>
        ),
      },
    };

    return { columns, sortByMap, reverseSortByMap };
  }, [
    caseListTable,
    caseTypeDefinitions,
    headlessWidgets,
    handleOnRowClick,
    setSelectedCase,
    setSelectedCaseIndex,
    onOpenExecuteRule,
    searchTerm,
    t,
  ]);
  return { columns, sortByMap, reverseSortByMap };
};
